import React, { useEffect, useState, useCallback } from "react";
import { listContactMessages, updateContactMessage } from "../../api/adminApi";

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const STATUSES = [
  { key: "", label: "All" },
  { key: "new", label: "New" },
  { key: "in_progress", label: "In Progress" },
  { key: "resolved", label: "Resolved" },
];

const STATUS_BADGE = {
  new: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  in_progress: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  resolved: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const AdminSupportTickets = () => {
  const [data, setData] = useState({
    results: [],
    count: 0,
    page: 1,
    page_size: 25,
    open_count: 0,
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await listContactMessages({ status: statusFilter, page }));
    } catch {
      setError("Couldn't load support tickets.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Optimistically update a ticket's status, roll back on failure.
  const setTicketStatus = async (id, next) => {
    setError("");
    const prev = data.results;
    setData((d) => ({
      ...d,
      results: d.results.map((m) => (m.id === id ? { ...m, status: next } : m)),
    }));
    try {
      await updateContactMessage(id, { status: next });
    } catch {
      setError("Update failed — reverted.");
      setData((d) => ({ ...d, results: prev }));
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.count || 0) / (data.page_size || 25)));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Support Tickets</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {data.count} total · {data.open_count} open. Messages submitted through the
          Contact Support form.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s.key || "all"}
            onClick={() => {
              setPage(1);
              setStatusFilter(s.key);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              statusFilter === s.key
                ? "bg-indigo-600 text-white border-indigo-600"
                : "border-gray-300 dark:border-white/15 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-500/10 text-red-500 text-sm">{error}</div>
      )}

      {loading ? (
        <Card>
          <p className="text-sm text-gray-500">Loading…</p>
        </Card>
      ) : data.results.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">No support tickets found.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.results.map((m) => (
            <Card key={m.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{m.name}</div>
                  <a
                    href={`mailto:${m.email}`}
                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    {m.email}
                  </a>
                  <div className="text-xs text-gray-500 mt-0.5">{fmtDate(m.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                      STATUS_BADGE[m.status] || "bg-gray-500/10 text-gray-500"
                    }`}
                  >
                    {m.status.replace("_", " ")}
                  </span>
                  <select
                    value={m.status}
                    onChange={(e) => setTicketStatus(m.id, e.target.value)}
                    className="px-2 py-1 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 text-sm"
                  >
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              </div>
              <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {m.message}
              </p>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/15 text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {data.page} of {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/15 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminSupportTickets;
